import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const acceptableSortDirections = ['asc', 'desc']

export interface useSortResult {
  sortBy: string | null
  setSortBy: (sortBy: string) => void
  sortDirection: 'asc' | 'desc'
  setSortDirection: (sortDirection: 'asc' | 'desc') => void
}

const useSort = (initialSortBy = '', initialSortDirection = 'desc'): useSortResult => {
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)

  const [sortBy, setCurrentSortBy] = useState(
    searchParams.has('sortBy') ? searchParams.get('sortBy') : initialSortBy,
  )
  const [sortDirection, setCurrentSortDirection] = useState(
    searchParams.has('sortDirection') ? searchParams.get('sortDirection') : initialSortDirection,
  )

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)

    // Clear the sort by if it's null or an empty string
    if ((sortBy ?? '') === '') {
      params.delete('sortBy')
    } else {
      params.set('sortBy', sortBy ?? '')
    }

    // Clear the sort direction if it's null or an empty string
    if ((sortDirection ?? '') === '') {
      params.delete('sortDirection')
    } else {
      params.set('sortDirection', sortDirection ?? '')
    }

    // Replace the current state in the history without adding a new entry
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`)
  }, [sortBy, sortDirection])

  // Reset the items per page if unexpected value
  useEffect(() => {
    if (sortDirection === null || !acceptableSortDirections.includes(sortDirection!)) {
      setCurrentSortDirection('desc')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection])

  const setSortBy = (sortBy: string) => {
    setCurrentSortBy(sortBy)
  }

  const setSortDirection = (sortDirection: 'asc' | 'desc') => {
    setCurrentSortDirection(sortDirection)
  }

  return {
    sortBy,
    setSortBy,
    sortDirection: sortDirection as useSortResult['sortDirection'],
    setSortDirection,
  }
}

export default useSort
